import React, { useState, useCallback } from "react";
import Modal from "../Modal/Modal";
import { Form } from "../Form/Form";
import type { Field } from "../Form/Form";
import type { CalendarEvent } from "./Calendar"; // Adicione este import se CalendarEvent não estiver disponível

interface CalendarEventModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (event: CalendarEvent) => void;
  initialDate?: Date;
  theme?: "light" | "dark";
}

const fields: Field[] = [
  { id: "summary", type: "input", label: "Título", required: true, placeholder: "Título do evento", inputType: "text" },
  { id: "location", type: "input", label: "Localização", placeholder: "Ex: Sala 1", inputType: "text" },
  { id: "dtstart", type: "datetime", label: "Início", required: true },
  { id: "dtend", type: "datetime", label: "Término" },
  { id: "duration", type: "input", label: "Duração (ex: PT1H30M)", placeholder: "PT1H30M", inputType: "text" },
  { id: "categories", type: "input", label: "Categorias", placeholder: "Ex: Reunião, Trabalho", inputType: "text" },
  { id: "status", type: "select", label: "Status", options: [
    { label: "Confirmado", value: "CONFIRMED" },
    { label: "Tentativo", value: "TENTATIVE" },
    { label: "Cancelado", value: "CANCELLED" }
  ]},
  { id: "transp", type: "select", label: "Transparência", options: [
    { label: "Ocupado", value: "OPAQUE" },
    { label: "Livre", value: "TRANSPARENT" }
  ]},
  { id: "priority", type: "input", label: "Prioridade", inputType: "number", min: 0, max: 9 },
  { id: "sequence", type: "input", label: "Sequência", inputType: "number", min: 0 },
  { id: "url", type: "input", label: "URL", placeholder: "https://...", inputType: "text" },
  { id: "organizer", type: "input", label: "Organizador (e-mail)", placeholder: "email@exemplo.com", inputType: "email" },
  { id: "attendee", type: "input", label: "Participantes (e-mails separados por vírgula)", placeholder: "a@b.com, c@d.com", inputType: "text" },
  { id: "class", type: "select", label: "Privacidade", options: [
    { label: "Público", value: "PUBLIC" },
    { label: "Privado", value: "PRIVATE" },
    { label: "Confidencial", value: "CONFIDENTIAL" }
  ]},
  { id: "attach", type: "upload", label: "Anexos", maxFiles: 3 },
  { id: "rrule", type: "input", label: "Recorrência (RRULE)", placeholder: "Ex: FREQ=WEEKLY;COUNT=10", inputType: "text" },
  { id: "alarm", type: "input", label: "Lembrete (ex: -PT10M)", placeholder: "Ex: -PT10M", inputType: "text" },
  // Descrição como wysiwyg, última e ocupando toda a linha
  { id: "description", type: "wysiwyg", label: "Descrição", placeholder: "Descrição do evento", colSpan: 2 }
];

const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
  open,
  onClose,
  onCreate,
  initialDate,
  theme = "light",
}) => {
  const [formData, setFormData] = useState<Partial<CalendarEvent>>({
    dtstart: initialDate ? initialDate.toISOString().slice(0, 16) : "",
  });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleCreate = useCallback(
    (data: Partial<CalendarEvent>) => {
      // Ensure required fields are present and types are correct
      const event: CalendarEvent = {
        ...data,
        dtstart: data.dtstart
          ? new Date(
              typeof data.dtstart === "string" || typeof data.dtstart === "number"
                ? data.dtstart
                : (data.dtstart as Date).toISOString()
            )
          : new Date(),
        dtend: data.dtend
          ? new Date(
              typeof data.dtend === "string" || typeof data.dtend === "number"
                ? data.dtend
                : (data.dtend as Date).toISOString()
            )
          : undefined,
        // Add any other required CalendarEvent fields with defaults if needed
      } as CalendarEvent;
      onCreate(event);
      onClose();
    },
    [onCreate, onClose]
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      theme={theme}
      className="max-w-4xl w-full font-grotesk"
    >
      <div style={{ maxHeight: "80vh", overflowY: "auto" }} className="font-grotesk">
        <Form
          config={{
            title: "Criar Evento",
            fields: [
              fields.map((f: Field): Field =>
                f.id === "dtstart" && initialDate
              ? { ...f, value: initialDate.toISOString().slice(0, 16) }
              : f
              ),
            ],
            buttons: { create: [{ title: "Criar", action: "submit" }] },
            columns: 2,
          }}
          collectionName="calendar-events"
          mode="create"
          initialValues={formData}
          onChange={(_id: string, _value: unknown, data: Partial<CalendarEvent>) => setFormData(data)}
          onSubmit={handleCreate}
          theme={theme}
        />
      </div>
    </Modal>
  );
};

export default CalendarEventModal;